import {
  fastedWindowConsentStep,
  morningOralMedsStep,
  requiresFastedWindow,
} from './fastedWindow';

// ============================================
// BRANDED WEIGHT LOSS (GLP-1) QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health "Standard Weightloss Questionnaire" (branded — NOT
// compounded): Wegovy/Ozempic (semaglutide), Zepbound/Mounjaro (tirzepatide),
// plus Foundayo (oral). Beluga visitType: weightloss (branded), follow-up
// visitType: weightlossfollowup. FE flow string: brandedWeightloss.
//
// BMI eligibility (doc header):
//   - Treatment-naive (new) patients: BMI >= 25, else disqualified
//   - Patients transferring in (already on therapy): BMI > 20
// The BMI gate is CROSS-ANSWER (depends on height-weight + recent-glp1-meds),
// so it's enforced in isDisqualifyingAnswer via the third `allAnswers` arg —
// see the form's handleAnswer.
//
// Color legend (Beluga source of truth):
//   BLACK  — required Q/A questions
//   BLUE   — conditional questions
//   PURPLE — named-field mappings (medicalConditions / selfReportedMeds /
//            allergies / sex); excluded from the Q/A section by the backend
//   RED    — fully disqualifying answers
//   PINK   — product routing → patientPreference (starter dose for new
//            patients and on-treatment patients without an Rx photo; oral
//            route → Foundayo; "Avoid Foundayo" interaction exclusions)
//
// NOTE (docx caveat): plain-text extraction strips colors, so the RED set for
// `glp1-conditions` + `diabetes-meds` mirrors the existing compounded /glp1
// flow. Flagged for Beluga verification in
// docs/BACKEND_BRANDED_WEIGHTLOSS_VISIT_PRD.md §Assumptions.

// ============================================
// CONSENT BODIES (verbatim-faithful from the questionnaire)
// ============================================

const GALLSTONES_CONSENT_BODY = `Use of GLP-1 medications, including semaglutide, tirzepatide, and similar products, has been associated with an increased risk of gallbladder or biliary issues, including the development or worsening of gallstones. If you have existing but asymptomatic gallstones, treatment may increase the chance that these stones become symptomatic, potentially leading to pain, inflammation, or the need for medical or surgical treatment. Notify your provider if you develop new upper abdominal pain, nausea, or vomiting during treatment. Knowing this risk, do you wish to continue?`;

const CHOLECYSTECTOMY_CONSENT_BODY = `You reported a history of gallbladder removal. This medication may still be appropriate but can alter fat and bile digestion, increasing the risk of side effects such as abdominal pain or diarrhea. It may also affect absorption of fat-soluble vitamins (A, D, E, and K); smaller meals, avoiding processed foods, and a daily multivitamin may help. Seek immediate care for severe abdominal pain, fever, or vomiting.`;

export const BRANDED_GLP_CONSENT_BODY = `Consent (GLP-1 and GLP-1/GIP):
You are requesting treatment with a GLP-1 (Ozempic, Wegovy, or compounded semaglutide) or GIP/GLP-1 receptor agonist (Mounjaro, Zepbound, or compounded tirzepatide) medication as part of your treatment plan for the management of weight or obesity. These medications work by mimicking the action of incretin hormones, which help regulate blood sugar levels, promote feeling full, and reduce food intake.
Weight loss medications are typically prescribed for individuals who are obese, or for individuals who are overweight with related medical conditions. Use of these medications in individuals who are overweight without an associated medical condition is considered off-label, meaning the medication is prescribed in a manner not specifically approved by the FDA. Off-label prescribing is a common and accepted medical practice when clinically appropriate and based on medical judgment.

Potential Benefits
- Weight loss or weight management
- Improved blood glucose control
- Reduced cardiovascular risk
- Potential improvement in overall metabolic health

Potential Side Effects
While these medications can be beneficial, they may also cause side effects. Although not common, these medications can result in emergency room visits, hospitalizations, or even death. Common and serious side effects include, but are not limited to:
- Common Side Effects: Nausea, Vomiting, Diarrhea, Constipation, Decreased appetite, Indigestion
- Serious Side Effects: Pancreatitis (inflammation of the pancreas), Hypoglycemia (low blood sugar) especially when used with other diabetes medications, Gallbladder disease (e.g., gallstones), Kidney problems, Allergic reactions (e.g., rash, itching, swelling), Gastroparesis (paralysis of the bowels)

Risks and Considerations
- Pancreatitis: There is a risk of developing pancreatitis. If you experience severe abdominal pain, nausea, or vomiting, you should contact your healthcare provider immediately.
- Thyroid Tumors: Animal studies have shown an increased risk of thyroid tumors with certain GLP-1 medications. Although this has not been confirmed in humans, please inform your healthcare provider if you have a history of thyroid cancer.
- Hypoglycemia: When taken with other diabetes medications, particularly insulin or sulfonylureas, there is a risk of low blood sugar. It is important that your provider knows if any of these medications are added to your regimen.
- Kidney Function: This medication may affect kidney function, particularly in patients with existing kidney disease. Regular monitoring of kidney function may be required.

Pregnancy Risk
These medications are not safe for use during pregnancy or while breastfeeding, as they may pose risks to a developing baby. The FDA advises the use of additional contraception because these medications may reduce the effectiveness of oral contraceptives. If you use oral birth control, you must continue it with a barrier method, such as condoms, for the first month after starting treatment and for one month after any dose increase. You may choose to switch to a non-oral contraceptive method, such as an intrauterine device or implant, prior to starting treatment. After stopping the medication, you should continue using effective contraception for at least two months to ensure the medication has fully cleared from your system before attempting to conceive.

Monitoring and Follow-up
You will require regular follow-up visits to monitor your response to the medication and to assess for any side effects. We may intermittently ask for full-body selfie images to ensure that your reported weight is consistent.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.
I acknowledge the potential benefits, risks, and side effects of GLP-1 or GIP/GLP-1 receptor agonist medications. I understand the importance of regular monitoring and follow-up appointments. I consent to the use of GLP-1 or GIP/GLP-1 receptor agonist medications as part of my treatment plan for overweight or obesity.`;

// ============================================
// PURPLE — named-field questions (mapped server-side, excluded from Q/A)
// ============================================

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea',
  question: 'Please identify all your current medical conditions',
  subtitle: 'Include chronic conditions, past surgeries, and any other relevant health issues.',
  placeholder: 'e.g., Hypertension, Asthma, Previous surgery, etc.',
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea',
  question: 'Please list all your current medications including dosages',
  placeholder: 'e.g., Lisinopril 10mg daily, Atorvastatin 20mg daily…',
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea',
  question: 'Please list all of your known allergies',
  placeholder: 'e.g., Penicillin (rash), Shellfish (anaphylaxis)…',
};

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What was your sex assigned at birth?',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
  ],
};

// BLUE — conditional, female only. "Yes" is a RED disqualifier.
const pregnancyQuestion = {
  id: 'pregnancy',
  type: 'single-select',
  question: 'Are you currently pregnant, breastfeeding or planning to become pregnant?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    {
      label:
        'No and I acknowledge that these medications are not safe to use during pregnancy or while breastfeeding, as they may pose risks to a developing baby.',
      value: 'no-acknowledge',
    },
  ],
};

// ---------- Vitals ----------
// HeightWeightBmiStep computes + displays BMI ("Your BMI is"). The eligibility
// gate is cross-answer (new vs transfer), so skipBmiCheck here and enforce in
// isDisqualifyingAnswer at 'recent-glp1-meds'.
const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'We use this to calculate your BMI and confirm program eligibility.',
  skipBmiCheck: true,
};

// ---------- Medical screening ----------

const glp1ConditionsQuestion = {
  id: 'glp1-conditions',
  type: 'multi-select',
  question: 'Do any of these apply to you?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Gastroparesis (Paralysis of your intestines)', value: 'gastroparesis', disqualifies: true },
    { label: 'Triglycerides over 600 at any point', value: 'triglycerides-over-600', disqualifies: true },
    { label: 'Pancreatic cancer or pancreatitis', value: 'pancreatic-cancer-pancreatitis', disqualifies: true },
    { label: 'Type 1 Diabetes/Insulin-dependent diabetes', value: 'type1-insulin-diabetes', disqualifies: true },
    { label: 'Moderate to severe diabetic retinopathy', value: 'diabetic-retinopathy', disqualifies: true },
    { label: 'Hypoglycemia (low blood sugar)', value: 'hypoglycemia', disqualifies: true },
    { label: 'Personal or family history of medullary thyroid cancer', value: 'medullary-thyroid-cancer', disqualifies: true },
    { label: 'Personal or family history of Multiple Endocrine Neoplasia (MEN-2) syndrome', value: 'men2-syndrome', disqualifies: true },
    { label: 'Anorexia or bulimia', value: 'anorexia-bulimia', disqualifies: true },
    { label: 'Liver failure/liver cirrhosis', value: 'liver-failure-cirrhosis', disqualifies: true },
    { label: 'Chronic Kidney Disease Stage 3b or greater', value: 'ckd-3b-or-greater', disqualifies: true },
    { label: 'Syndrome of Inappropriate Antidiuretic hormone', value: 'siadh', disqualifies: true },
    { label: 'Current symptomatic gallstones', value: 'symptomatic-gallstones', disqualifies: true },
    { label: 'Gastric bypass in the past 6 months', value: 'gastric-bypass-6mo', disqualifies: true },
    { label: 'Current gallstones without symptoms', value: 'gallstones-no-symptoms' },
    { label: 'Past removal of your gallbladder', value: 'cholecystectomy' },
    { label: 'Hypothyroidism, Hyperthyroidism, or Thyroid Issues', value: 'thyroid-issues' },
    { label: 'None of the above', value: 'none' },
  ],
};

// BLUE — only when 'gallstones-no-symptoms' selected. Decline = RED.
const gallstonesConsentQuestion = {
  id: 'gallstones-consent',
  type: 'consent-dropdown',
  question: 'Gallstones Consent',
  consentText: GALLSTONES_CONSENT_BODY,
  options: [
    { label: 'I have read and understand the information above and wish to proceed', value: 'consent' },
    { label: 'I do NOT wish to continue', value: 'decline', disqualifies: true },
  ],
};

// BLUE — only when 'cholecystectomy' selected. Decline = RED.
const cholecystectomyConsentQuestion = {
  id: 'cholecystectomy-consent',
  type: 'consent-dropdown',
  question: 'Cholecystectomy Consent',
  consentText: CHOLECYSTECTOMY_CONSENT_BODY,
  options: [
    { label: 'I have read and understand the information above and wish to proceed', value: 'consent' },
    { label: 'I do NOT wish to continue', value: 'decline', disqualifies: true },
  ],
};

// BLUE — only when gallstones/cholecystectomy/thyroid selected.
const glp1ConditionsDetailsQuestion = {
  id: 'glp1-conditions-details',
  type: 'textarea',
  question: 'Please tell us more about your medical condition(s) that you selected',
  placeholder: 'Tell us more…',
};

const glp1AllergyQuestion = {
  id: 'glp1-allergy',
  type: 'single-select',
  question:
    'Are you allergic to any GLP-1 medications such as Ozempic/Wegovy (Semaglutide), Mounjaro/Zepbound (Tirzepatide), Saxenda (Liraglutide), or Trulicity (Dulaglutide)?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

// RED assumed for Yes (hypoglycemia risk with insulin/sulfonylureas/DPP-4) —
// mirrors the compounded /glp1 flow; flagged in the PRD for Beluga verify.
const diabetesMedsQuestion = {
  id: 'diabetes-meds',
  type: 'single-select',
  question: 'Do you take any of the following medications?',
  subtitle:
    'Insulin; Glimepiride (Amaryl); Meglitinides such as repaglinide or nateglinide; Glipizide (Glucotrol and Glucotrol XL); Glyburide (Micronase, Glynase, and Diabeta); Sitagliptin; Saxagliptin; Linagliptin; Alogliptin',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

// Included because we offer Foundayo (doc: "Include the following question
// ONLY if you wish to provide Foundayo"). Avoid-items are PINK routing (exclude
// the oral/Foundayo product), NOT disqualifiers.
export const FOUNDAYO_AVOID_VALUES = ['cyp-inhibitors', 'seizure-meds-sjw', 'simvastatin-30'];

const foundayoInteractionsQuestion = {
  id: 'foundayo-interactions',
  type: 'multi-select',
  question: 'Are you taking any medications that may interact with this medication?',
  subtitle: 'Select all that apply.',
  options: [
    {
      label: 'Certain antibiotics, antifungals, or HIV medications (examples: ketoconazole, itraconazole, and/or ritonavir)',
      value: 'cyp-inhibitors',
    },
    {
      label: "Seizure medications (examples: carbamazepine, phenytoin, phenobarbital) or St. John's Wort",
      value: 'seizure-meds-sjw',
    },
    { label: 'Simvastatin/Zocor with a dose of 30mg or higher', value: 'simvastatin-30' },
    { label: 'Birth control pills or other oral hormonal contraceptives', value: 'oral-contraceptives' },
    { label: 'None of the above', value: 'none' },
  ],
};

// ---------- New vs already-on-treatment branch ----------

const recentGlp1MedsQuestion = {
  id: 'recent-glp1-meds',
  type: 'single-select',
  question: 'Have you taken any weight loss medications in the past 4 weeks?',
  options: [
    { label: 'Semaglutide (Ozempic, Wegovy, Rybelsus)', value: 'semaglutide' },
    { label: 'Tirzepatide (Zepbound, Mounjaro)', value: 'tirzepatide' },
    { label: 'None of these', value: 'none' },
  ],
};

// BLUE — New Patient branch only. 'no-need-oral' routes to the oral product
// (Foundayo); it only disqualifies when Foundayo is excluded by interactions.
const injectionAbilityQuestion = {
  id: 'injection-ability',
  type: 'single-select',
  question: 'Are you able to safely give yourself injections or have someone help you?',
  options: [
    { label: "No, I'd need an oral option instead", value: 'no-need-oral' },
    { label: 'Yes, I can inject myself or have reliable help', value: 'yes-can-inject' },
  ],
};

// ---------- Already On Treatment branch ----------

const lastDoseTimingQuestion = {
  id: 'last-dose-timing',
  type: 'single-select',
  question: 'When did you last take your prescribed weight loss medication?',
  options: [
    { label: 'Within the past week', value: 'past-week' },
    { label: 'Within the past 2-4 weeks', value: '2-4-weeks' },
  ],
};

// BLUE — only when last dose was 2-4 weeks ago.
const doseDecreaseAckQuestion = {
  id: 'dose-decrease-ack',
  type: 'single-select',
  question: 'How would you like to proceed?',
  subtitle:
    'Because these medications build up gradually in your system, restarting at a high dose after a break can increase the risk of side effects. For safety, we recommend continuing at a lower dose (usually about 50% of your previous dose) and gradually increasing if appropriate.',
  options: [
    { label: 'I understand and agree to continue at a lower dose', value: 'agree-lower-dose' },
    { label: 'I would like to discuss this further with the provider', value: 'discuss-with-provider' },
  ],
};

const lastInjectionDateQuestion = {
  id: 'last-injection-date',
  type: 'text',
  question: 'What was the approximate date you last injected your GLP-1 medication?',
  placeholder: 'e.g., 2026-05-28',
};

const currentMedDoseQuestion = {
  id: 'current-med-dose',
  type: 'single-select',
  question: 'Which medication and dose most closely matches your most recent dose?',
  options: [
    { label: 'Semaglutide/Ozempic/Wegovy 0.25mg', value: 'sema-0.25' },
    { label: 'Semaglutide/Ozempic/Wegovy 0.5mg', value: 'sema-0.5' },
    { label: 'Semaglutide/Ozempic/Wegovy 1mg', value: 'sema-1' },
    { label: 'Semaglutide/Ozempic/Wegovy 1.5 or 1.7mg', value: 'sema-1.5-1.7' },
    { label: 'Semaglutide/Ozempic/Wegovy 2.0mg', value: 'sema-2.0' },
    { label: 'Semaglutide/Ozempic/Wegovy 2.4mg', value: 'sema-2.4' },
    { label: 'Tirzepatide/Mounjaro/Zepbound 2.5mg', value: 'tirz-2.5' },
    { label: 'Tirzepatide/Mounjaro/Zepbound 5mg', value: 'tirz-5' },
    { label: 'Tirzepatide/Mounjaro/Zepbound 7.5mg', value: 'tirz-7.5' },
    { label: 'Tirzepatide/Mounjaro/Zepbound 10mg', value: 'tirz-10' },
    { label: 'Tirzepatide/Mounjaro/Zepbound 12.5mg', value: 'tirz-12.5' },
    { label: 'Tirzepatide/Mounjaro/Zepbound 15mg', value: 'tirz-15' },
  ],
};

const medSideEffectsQuestion = {
  id: 'med-side-effects',
  type: 'single-select',
  question: 'Have you experienced side effects from your current medication?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

// BLUE — only when med-side-effects === 'yes'.
const medSideEffectsDetailsQuestion = {
  id: 'med-side-effects-details',
  type: 'textarea',
  question: "Please describe the side effects that you've experienced",
  placeholder: 'Describe the side effects…',
};

const continuePreferenceQuestion = {
  id: 'continue-preference',
  type: 'single-select',
  question: 'How would you like to continue your treatment?',
  options: [
    { label: 'Stay at the same dose or equivalent dose', value: 'same-dose' },
    {
      label: "Increase the dose if a higher one is available, or continue with my current dose if it's already at the maximum",
      value: 'increase-dose',
    },
    { label: 'Decrease dose', value: 'decrease-dose' },
  ],
};

// 'no' → starter-dose routing (PINK): patientPreference becomes the starter
// dose of the requested medication, resolved backend-side from planId.
const rxPhotoAvailableQuestion = {
  id: 'rx-photo-available',
  type: 'single-select',
  question: 'Do you have a picture of your current prescription? We need this photograph in order to validate your current dosage.',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

// BLUE — only when rx-photo-available === 'yes'. Relayed post-submit via
// /external/receivePhotos (same pipeline as identity-verification).
const rxPhotoUploadQuestion = {
  id: 'rx-photo-upload',
  type: 'file-upload',
  question: 'Please upload a picture of the prescription or bottle of your current GLP-1/GIP medication',
  optional: true,
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  subtitle: 'Feel free to share any concerns or questions.',
  placeholder: 'Type your questions or concerns here…',
};

// ---------- Contact / shipping / identity ----------

const basicContactQuestion = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send updates about your treatment.",
};

const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Shipping Address',
};

// ---------- Pharmacy choice (branded meds are retail-fillable) ----------
// Two destinations only (owner 2026-10-18): the partner-pharmacy ship option
// ('default') was removed — its med retail price undercut nobody. Both paths
// left charge the consult fee only; the patient pays the pharmacy for the drug.
// 'own' → patient picks from Beluga's directory (POST /external/pharmacies via
// the backend proxy) — see docs/BACKEND_BRANDED_WEIGHTLOSS_VISIT_PRD.md §4a.
// 'manufacturer' → LillyDirect / NovoCare, resolved per SKU at checkout
// (data/manufacturerPharmacies.js); no search slide.
const pharmacyChoiceQuestion = {
  id: 'pharmacy-choice',
  type: 'single-select',
  question: 'Where would you like your prescription sent?',
  subtitle: 'Brand-name medications can be filled by the manufacturer\'s own pharmacy or a local pharmacy of your choice.',
  options: [
    {
      label: 'Send it to the manufacturer\'s pharmacy (LillyDirect or NovoCare) — ships to my door',
      value: 'manufacturer',
    },
    {
      label: 'Send it to my own pharmacy — I will pick it up locally',
      value: 'own',
    },
  ],
};

// BLUE — only when pharmacy-choice === 'own'.
const pharmacySearchQuestion = {
  id: 'pharmacy-search',
  type: 'pharmacy-select',
  question: 'Find your pharmacy',
  subtitle: 'Search by name, city, state, or ZIP and select the pharmacy that should receive your prescription.',
};

const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle:
    "Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible. If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card. Make sure the photo is clear and legible.",
  // Required by Beluga — ID photo cannot be skipped (2026-06-27). (rx-photo-upload stays optional.)
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'GLP-1 and GLP-1/GIP — Informed Consent',
  consentText: BRANDED_GLP_CONSENT_BODY,
  options: [
    { label: 'I have read and understand the above information and I wish to continue', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true },
  ],
};

const checkoutStep = {
  id: 'checkout',
  type: 'checkout',
  question: 'Select Your Treatment Plan',
};

const completeStep = {
  id: 'complete',
  type: 'complete',
  question: 'Complete',
};

// ============================================
// FULL FLOW (canonical questionnaire order)
// ============================================

export const brandedWeightlossFlow = [
  // PURPLE / named-field
  medicalConditionsQuestion,
  selfReportedMedsQuestion,
  allergiesQuestion,
  sexAtBirthQuestion,
  pregnancyQuestion, // conditional: sex-at-birth === 'female'

  heightWeightQuestion,

  // Medical screening
  glp1ConditionsQuestion,
  gallstonesConsentQuestion, // conditional: glp1-conditions includes 'gallstones-no-symptoms'
  cholecystectomyConsentQuestion, // conditional: glp1-conditions includes 'cholecystectomy'
  glp1ConditionsDetailsQuestion, // conditional: any of the 3 non-RED conditions selected
  glp1AllergyQuestion,
  diabetesMedsQuestion,
  foundayoInteractionsQuestion,

  // Branch selector
  recentGlp1MedsQuestion,

  // New Patient branch
  injectionAbilityQuestion, // conditional: recent-glp1-meds === 'none'

  // Already On Treatment branch
  lastDoseTimingQuestion, // conditional: on treatment
  doseDecreaseAckQuestion, // conditional: last-dose-timing === '2-4-weeks'
  lastInjectionDateQuestion, // conditional: on treatment
  currentMedDoseQuestion, // conditional: on treatment
  medSideEffectsQuestion, // conditional: on treatment
  medSideEffectsDetailsQuestion, // conditional: med-side-effects === 'yes'
  continuePreferenceQuestion, // conditional: on treatment
  rxPhotoAvailableQuestion, // conditional: on treatment
  rxPhotoUploadQuestion, // conditional: rx-photo-available === 'yes'

  additionalQuestionsQuestion,

  // Contact / shipping / pharmacy / identity
  basicContactQuestion,
  shippingAddressStep,
  pharmacyChoiceQuestion,
  pharmacySearchQuestion, // conditional: pharmacy-choice === 'own'
  identityVerificationStep,

  mainConsentQuestion,
  checkoutStep,
  completeStep,
];

export const brandedWeightlossFlowById = brandedWeightlossFlow.reduce((acc, slide) => {
  acc[slide.id] = slide;
  return acc;
}, {});

// ============================================
// Branch helpers
// ============================================

export const isOnTreatment = (answers) =>
  answers['recent-glp1-meds'] === 'semaglutide' || answers['recent-glp1-meds'] === 'tirzepatide';

export const isNewPatient = (answers) => answers['recent-glp1-meds'] === 'none';

export const avoidsFoundayo = (answers) => {
  const sel = Array.isArray(answers['foundayo-interactions']) ? answers['foundayo-interactions'] : [];
  return FOUNDAYO_AVOID_VALUES.some((v) => sel.includes(v));
};

const bmiFromAnswers = (answers) => {
  const hw = answers['height-weight'];
  if (!hw || typeof hw !== 'object') return null;
  const bmi = parseFloat(hw.bmi);
  return Number.isFinite(bmi) ? bmi : null;
};

// ============================================
// Dynamic flow builder — strips conditionals that have not fired
// ============================================

const ON_TREATMENT_IDS = [
  'last-dose-timing',
  'dose-decrease-ack',
  'last-injection-date',
  'current-med-dose',
  'med-side-effects',
  'med-side-effects-details',
  'continue-preference',
  'rx-photo-available',
  'rx-photo-upload',
];

// §resume-invariant (2026-09-04, AMENDED 2026-09-05) — every conditional below
// must branch only on inputs PASSED INTO THIS FUNCTION. Nothing here may reach
// out to the URL, a prop, or module state on its own.
//
// Why: BrandedWeightlossForm persists {currentStep, answers} to ONE localStorage
// key and its resume modal restores that blob wholesale. That was safe while this
// builder was a pure function of the blob — the flow is recomputed from the
// restored answers, so whatever it strips, it strips for reasons still true of
// those answers. Self-consistent by construction.
//
// §6A ENDED THAT, deliberately and in the way the invariant prescribed. The
// fasted-window acknowledgement is conditional on the PRODUCT, which comes from
// the URL and is not in the blob. The invariant offered two resolutions — feed
// the outside input into the recomputation, or scope the resume to exclude what
// depends on it — and this takes the first: `product` is now an explicit second
// argument, so the flow is still recomputed from its full input on every restore.
//
// The bug that would have followed from the other choice, and the test that pins
// it shut (brandedWeightlossData.test.js): resume a foundayo-pill answer set onto
// wegovy-pill and the acknowledgement must STILL be asked, because the fasted
// restriction is a property of the CURRENT product, not the saved one. Read the
// product from module state or a closure here and that test starts passing for
// the wrong reason.
//
// The rule stands for the next one: a new conditional keyed on anything outside
// `answers` must arrive as an argument. See docs/FE_ACK_BRANDED_PRODUCT_LINKS.md §2a.
export const buildDynamicBrandedWeightlossFlow = (answers, product = null) => {
  let flow = [...brandedWeightlossFlow];

  // §6A: product-conditional, and therefore driven by the `product` ARGUMENT —
  // never by anything this module could reach on its own. Dark until the copy
  // clears Beluga review (requiresFastedWindow reads the flag too).
  if (requiresFastedWindow(product)) {
    const at = flow.findIndex((q) => q.id === 'checkout');
    const insertAt = at === -1 ? flow.length : at;
    flow = [
      ...flow.slice(0, insertAt),
      fastedWindowConsentStep,
      morningOralMedsStep,
      ...flow.slice(insertAt),
    ];
  }
  const arr = (id) => (Array.isArray(answers[id]) ? answers[id] : []);

  // pregnancy — female only
  if (answers['sex-at-birth'] !== 'female') {
    flow = flow.filter((q) => q.id !== 'pregnancy');
  }

  // gallstones / cholecystectomy consents + condition details
  const conditions = arr('glp1-conditions');
  if (!conditions.includes('gallstones-no-symptoms')) {
    flow = flow.filter((q) => q.id !== 'gallstones-consent');
  }
  if (!conditions.includes('cholecystectomy')) {
    flow = flow.filter((q) => q.id !== 'cholecystectomy-consent');
  }
  const detailTriggers = ['gallstones-no-symptoms', 'cholecystectomy', 'thyroid-issues'];
  if (!detailTriggers.some((v) => conditions.includes(v))) {
    flow = flow.filter((q) => q.id !== 'glp1-conditions-details');
  }

  // pharmacy-search — only when the patient wants their own pharmacy
  if (answers['pharmacy-choice'] !== 'own') {
    flow = flow.filter((q) => q.id !== 'pharmacy-search');
  }

  // Branches: until recent-glp1-meds is answered, hide both branches.
  if (!isNewPatient(answers)) {
    flow = flow.filter((q) => q.id !== 'injection-ability');
  }
  if (!isOnTreatment(answers)) {
    flow = flow.filter((q) => !ON_TREATMENT_IDS.includes(q.id));
  } else {
    // On-treatment sub-conditionals
    if (answers['last-dose-timing'] !== '2-4-weeks') {
      flow = flow.filter((q) => q.id !== 'dose-decrease-ack');
    }
    if (answers['med-side-effects'] !== 'yes') {
      flow = flow.filter((q) => q.id !== 'med-side-effects-details');
    }
    if (answers['rx-photo-available'] !== 'yes') {
      flow = flow.filter((q) => q.id !== 'rx-photo-upload');
    }
  }

  return flow;
};

// ============================================
// Per-answer disqualifier resolver
// ============================================
// Third arg `allAnswers` enables the cross-answer BMI gate (new >= 25,
// transfer > 20) and the oral-route-without-Foundayo check.

const MULTI_SELECT_DISQUALIFIERS = {
  'glp1-conditions': [
    'gastroparesis',
    'triglycerides-over-600',
    'pancreatic-cancer-pancreatitis',
    'type1-insulin-diabetes',
    'diabetic-retinopathy',
    'hypoglycemia',
    'medullary-thyroid-cancer',
    'men2-syndrome',
    'anorexia-bulimia',
    'liver-failure-cirrhosis',
    'ckd-3b-or-greater',
    'siadh',
    'symptomatic-gallstones',
    'gastric-bypass-6mo',
  ],
};

const DISQUALIFY_REASONS = {
  pregnancy:
    'GLP-1 medications are not safe to use during pregnancy or while breastfeeding. We cannot continue with this submission.',
  'glp1-conditions':
    'Based on your medical history, GLP-1 medications are not safe for you right now. Please discuss alternatives with your primary care provider.',
  'glp1-allergy':
    'Because of your allergy to GLP-1 medications, we cannot safely prescribe this treatment.',
  'diabetes-meds':
    'These medications cannot be combined safely with your current diabetes medications due to the risk of low blood sugar. Please consult the prescribing provider.',
  'gallstones-consent': 'You have indicated that you do not wish to continue with this treatment.',
  'cholecystectomy-consent': 'You have indicated that you do not wish to continue with this treatment.',
  'main-consent': 'You have indicated that you do not wish to continue with this treatment.',
  'bmi-new':
    'Based on your BMI, you are not eligible for this program as a new patient (BMI of 25 or higher is required).',
  'bmi-transfer':
    'Based on your BMI, continuing GLP-1 therapy is not appropriate at this time (BMI must be above 20).',
  'oral-unavailable':
    'An oral option is not appropriate for you because of a medication interaction, and injectable therapy requires the ability to self-inject. We cannot proceed right now.',
};

export const isDisqualifyingAnswer = (questionId, answer, allAnswers = {}) => {
  const singleRejects = {
    pregnancy: 'yes',
    'glp1-allergy': 'yes',
    'diabetes-meds': 'yes',
    'gallstones-consent': 'decline',
    'cholecystectomy-consent': 'decline',
    'main-consent': 'decline',
  };
  if (singleRejects[questionId] !== undefined && answer === singleRejects[questionId]) {
    return { disqualified: true, reason: DISQUALIFY_REASONS[questionId] };
  }

  if (Array.isArray(answer) && MULTI_SELECT_DISQUALIFIERS[questionId]) {
    const hit = MULTI_SELECT_DISQUALIFIERS[questionId].some((v) => answer.includes(v));
    if (hit) {
      return { disqualified: true, reason: DISQUALIFY_REASONS[questionId] };
    }
  }

  // Cross-answer BMI gate — evaluated once the branch is known.
  if (questionId === 'recent-glp1-meds') {
    const bmi = bmiFromAnswers(allAnswers);
    if (bmi !== null) {
      if (answer === 'none' && bmi < 25) {
        return { disqualified: true, reason: DISQUALIFY_REASONS['bmi-new'] };
      }
      if ((answer === 'semaglutide' || answer === 'tirzepatide') && bmi <= 20) {
        return { disqualified: true, reason: DISQUALIFY_REASONS['bmi-transfer'] };
      }
    }
  }

  // Oral route requested but Foundayo excluded by interactions → no product.
  if (questionId === 'injection-ability' && answer === 'no-need-oral' && avoidsFoundayo(allAnswers)) {
    return { disqualified: true, reason: DISQUALIFY_REASONS['oral-unavailable'] };
  }

  return { disqualified: false };
};

// ============================================
// TREATMENT PLANS (placeholder pricing — backend reconciles SKU + medId on
// submission, same pattern as the WSH/MSH/TRT flows). PINK routing notes:
//   - New patients + on-treatment patients without an Rx photo → backend sets
//     patientPreference to the STARTER dose of the selected brand.
//   - Oral route (injection-ability = no-need-oral) → Foundayo only.
//   - Foundayo category hidden when avoid-interactions selected.
// ============================================

export const brandedWeightlossPlans = [
  {
    category: 'Semaglutide (Brand)',
    description: 'Brand-name semaglutide injectables',
    productKey: 'semaglutide-brand',
    plans: [
      {
        plan_slug: 'bwl-wegovy-monthly',
        plan_name: 'Wegovy — Monthly',
        plan_price: 449,
        plan_content:
          'Brand-name Wegovy (semaglutide),Weekly injection,Monthly supply,Provider consultation,Free shipping',
      },
      {
        plan_slug: 'bwl-ozempic-monthly',
        plan_name: 'Ozempic — Monthly',
        plan_price: 449,
        plan_content:
          'Brand-name Ozempic (semaglutide),Weekly injection,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'Tirzepatide (Brand)',
    description: 'Brand-name tirzepatide injectables',
    productKey: 'tirzepatide-brand',
    plans: [
      {
        plan_slug: 'bwl-zepbound-monthly',
        plan_name: 'Zepbound — Monthly',
        plan_price: 549,
        plan_content:
          'Brand-name Zepbound (tirzepatide),Weekly injection,Monthly supply,Provider consultation,Free shipping',
      },
      {
        plan_slug: 'bwl-mounjaro-monthly',
        plan_name: 'Mounjaro — Monthly',
        plan_price: 549,
        plan_content:
          'Brand-name Mounjaro (tirzepatide),Weekly injection,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'Oral GLP-1',
    description: 'Needle-free oral options',
    productKey: 'oral-glp1',
    plans: [
      {
        plan_slug: 'bwl-foundayo-monthly',
        plan_name: 'Foundayo — Monthly',
        plan_price: 299,
        plan_content:
          'Foundayo oral GLP-1,No injections,Monthly supply,Provider consultation,Free shipping',
      },
      // Wegovy Tablets (oral semaglutide). BE-confirmed price $399 (plan
      // bwl-wegovy-tablets-monthly seeded 2026-07-01; BE is authoritative — the
      // branded checkout should read plan_price from /client-plan/* rather than
      // this literal; tracked for the BE-sourced-pricing migration).
      {
        plan_slug: 'bwl-wegovy-tablets-monthly',
        plan_name: 'Wegovy Tablets — Monthly',
        plan_price: 399,
        plan_content:
          'Brand-name Wegovy oral tablets (semaglutide),No injections,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
];

export default brandedWeightlossFlow;
